import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Network, Users, Upload, Settings, CircleUser, LogOut, BarChart3, Globe2 } from "lucide-react";
import { initials, ROLE_COLORS } from "@/lib/orgUtils";

const NAV = [
  { to: "/", label: "Charts", icon: Network, roles: ["super_admin", "admin", "editor", "viewer"] },
  { to: "/directory", label: "Directory", icon: Users, roles: ["super_admin", "admin", "editor", "viewer"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["super_admin", "admin", "editor"] },
  { to: "/imports", label: "Imports", icon: Upload, roles: ["super_admin", "admin", "editor"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["super_admin", "admin", "editor"] },
  { to: "/profile", label: "My Profile", icon: CircleUser, roles: ["super_admin", "admin", "editor", "viewer"] },
  { to: "/operator", label: "Operator", icon: Globe2, operatorOnly: true },
];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const rc = ROLE_COLORS[user?.role] || ROLE_COLORS.viewer;

  useEffect(() => {
    api.get("/org").then((r) => setOrgName(r.data?.name || "")).catch(() => {});
  }, []);

  return (
    <div className="h-screen flex flex-col bg-[#F6F7FB]">
      <header className="h-14 shrink-0 bg-white border-b border-[#181B34]/10 flex items-center px-4 gap-3 z-30">
        <button data-testid="logo-home-btn" onClick={() => navigate("/")} className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#6161FF] flex items-center justify-center">
            <Network size={17} className="text-white" />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight">
            Organa<span className="text-[#9699AD] font-medium text-xs ml-1.5">by Zefficacy</span>
          </span>
        </button>
        {orgName && (
          <span data-testid="header-org-name" className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F0F3FF] text-xs font-black text-[#4b4e63] max-w-48 truncate">
            {orgName}
          </span>
        )}
        <nav className="flex items-center gap-1 ml-6">
          {NAV.filter((n) => (n.operatorOnly ? user?.platform_operator : n.roles.includes(user?.role))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold ${
                  isActive ? "bg-[#6161FF] text-white" : "text-[#4b4e63] hover:bg-[#F0F3FF]"
                }`
              }
            >
              <n.icon size={15} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold" style={{ background: rc.bg, color: rc.text }}>
            {rc.label}
          </span>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#F0F3FF] text-[#6161FF] flex items-center justify-center text-xs font-bold">
              {initials(user?.name || user?.email)}
            </span>
            <span className="text-sm font-semibold hidden md:block" data-testid="header-user-name">{user?.name || user?.email}</span>
          </div>
          <button data-testid="logout-btn" onClick={logout} title="Sign out"
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#676a82] hover:bg-[#E2445C]/10 hover:text-[#E2445C]">
            <LogOut size={16} />
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
